import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/** Client polls artifacts.preview this often while the status is pending. */
export const PREVIEW_POLL_INTERVAL_MS = 2_000;
/** After this long without ready the client shows the preview as failed. */
export const PREVIEW_POLL_TIMEOUT_MS = 60_000;
/** Converted PDFs above this page count end as failed. */
export const PREVIEW_MAX_PAGES = 300;
/** Converted PDFs above this size end as failed. */
export const PREVIEW_MAX_PDF_BYTES = 50 * 1024 * 1024;

/** Longest comment on one annotation, in characters. */
export const ANNOTATION_COMMENT_MAX_LENGTH = 2_000;
/** Most annotations on one artifact version. */
export const ANNOTATIONS_PER_VERSION_MAX = 200;
/** Most annotations sent to the bot in one annotations.submit. */
export const ANNOTATION_SUBMIT_MAX = 50;
/** Most points of a freehand stroke; the other shapes have exactly two. */
export const ANNOTATION_FREEHAND_POINTS_MAX = 500;
/** Largest excerpt image per annotation, in decoded bytes. */
export const ANNOTATION_EXCERPT_PNG_MAX_BYTES = 512 * 1024;
/** Base64 length of the largest excerpt image; checked before decoding. */
export const ANNOTATION_EXCERPT_PNG_MAX_BASE64_LENGTH =
  Math.ceil(ANNOTATION_EXCERPT_PNG_MAX_BYTES / 3) * 4;
/** Longest text under one shape, taken from the pdf.js text layer. */
export const ANNOTATION_EXCERPT_TEXT_MAX_LENGTH = 1_000;

/**
 * State of the PDF rendering of one artifact version. PDFs are ready at once;
 * docx/xlsx/pptx convert on the first call. pages holds each page size in PDF
 * points, in page order, when status is ready.
 */
export const ArtifactPreviewSchema = z.object({
  status: z.enum(["ready", "pending", "failed", "unsupported"]),
  pageCount: z.number().int().min(1).max(PREVIEW_MAX_PAGES).optional(),
  pages: z
    .array(z.object({ width: z.number().positive(), height: z.number().positive() }))
    .max(PREVIEW_MAX_PAGES)
    .optional(),
  /** Plain-language reason when status is failed. */
  error: z.string().max(500).optional(),
});
/** Preview state as the client receives it. */
export type ArtifactPreview = z.infer<typeof ArtifactPreviewSchema>;

/** A point relative to the page: 0..1 each, origin top left. */
export const AnnotationPointSchema = z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]);

const twoPoints = z.array(AnnotationPointSchema).length(2);
const color = z.string().regex(/^#[0-9a-f]{6}$/i);

/** Drawn shape; rect, ellipse and highlight span two corners, arrow runs from tail to head. */
export const AnnotationShapeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("rect"), points: twoPoints, color }),
  z.object({ kind: z.literal("ellipse"), points: twoPoints, color }),
  z.object({ kind: z.literal("arrow"), points: twoPoints, color }),
  z.object({ kind: z.literal("highlight"), points: twoPoints, color }),
  z.object({
    kind: z.literal("freehand"),
    points: z.array(AnnotationPointSchema).min(2).max(ANNOTATION_FREEHAND_POINTS_MAX),
    color,
  }),
]);
/** One drawn shape in page-relative coordinates. */
export type AnnotationShape = z.infer<typeof AnnotationShapeSchema>;

/** Open until the bot (or a reader) marks it done. */
export const AnnotationStatusSchema = z.enum(["open", "done"]);
/** Whether an annotation still waits for the bot. */
export type AnnotationStatus = z.infer<typeof AnnotationStatusSchema>;

const comment = z.string().max(ANNOTATION_COMMENT_MAX_LENGTH);

/**
 * A mark on one page of one artifact version. Stored apart from the file;
 * artifactId names the version, version repeats its number for display.
 */
export const AnnotationSchema = z.object({
  id: Id,
  artifactId: Id,
  version: z.number().int(),
  /** 1-based page of the converted PDF. */
  page: z.number().int().min(1).max(PREVIEW_MAX_PAGES),
  shape: AnnotationShapeSchema,
  comment,
  authorUserId: Id,
  status: AnnotationStatusSchema,
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
/** One annotation as the client receives it. */
export type Annotation = z.infer<typeof AnnotationSchema>;

/** Input of annotations.create. */
export const AnnotationCreateInputSchema = z.object({
  artifactId: Id,
  page: z.number().int().min(1).max(PREVIEW_MAX_PAGES),
  shape: AnnotationShapeSchema,
  comment,
});

/** Input of annotations.update; shape and comment only by the author, status by any reader. */
export const AnnotationUpdateInputSchema = z.object({
  id: Id,
  shape: AnnotationShapeSchema.optional(),
  comment: comment.optional(),
  status: AnnotationStatusSchema.optional(),
});

/** What the client saw under one annotation, sent along with annotations.submit. */
export const AnnotationExcerptSchema = z.object({
  annotationId: Id,
  /** PNG of the marked area, base64. */
  png: z.string().max(ANNOTATION_EXCERPT_PNG_MAX_BASE64_LENGTH),
  text: z.string().max(ANNOTATION_EXCERPT_TEXT_MAX_LENGTH),
});

/** Input of annotations.submit: the chosen annotations of one artifact version. */
export const AnnotationSubmitInputSchema = z.object({
  artifactId: Id,
  annotationIds: z.array(Id).min(1).max(ANNOTATION_SUBMIT_MAX),
  excerpts: z.array(AnnotationExcerptSchema).max(ANNOTATION_SUBMIT_MAX),
});

/** Where the request to the bot landed. */
export const AnnotationSubmitOutputSchema = z.object({
  botId: Id,
  threadId: Id,
  messageId: Id,
});
